/**
 * The F&O Performance Console's shared vocabulary: lookbacks, classification
 * families and their tones, coverage formatting, the reason gate's rules, and
 * the client-side derivation of a record's reason text (concept §5/§6).
 *
 * This module imports no runtime values on purpose. It is exercised directly by
 * `tests/fo-console-format.test.mjs` under Node's own type stripping (the
 * project's frontend gates are tsc, eslint, vite build and `node --test`), so
 * it must stay free of JSX, path aliases at runtime, and DS imports. Type-only
 * imports are erased and are fine.
 *
 * Honesty rules enforced here rather than in the panels, so no panel can
 * disagree:
 *
 * - `null` renders as an em dash, never as `0`. A record whose network block is
 *   absent is **unattributed**, and `UNATTRIBUTED_CHIP` is the text every cell
 *   that would otherwise be empty shows — an empty TTFB cell reads as "fine".
 * - Coverage travels with every ratio (`formatCoverage`), and its tone follows
 *   the design rules (danger-toned below threshold) even though the estate's
 *   coverage today is low by construction — DKTAA and CZPRI are not behind Cato
 *   yet, and the caption says so wherever the meter appears.
 * - `http_*` metrics are not part of the contract (D3) and nothing here
 *   formats one. "0 HTTP errors" would be a fabrication.
 */

import type {
  FoConsoleCollectTask,
  FoConsoleCompanyLoad,
  FoConsoleCoverage,
  FoConsoleDevice,
  FoConsoleDeviceFacts,
  FoConsoleLookback,
  FoConsolePinpointCoverage,
  FoConsolePinpointFinding,
  FoConsoleReportLookback,
  FoConsoleReportStatus,
  FoConsoleRumBlockV2,
  FoConsoleRumCoverage,
  FoConsoleRumJoinKind,
  FoConsoleRumStatus,
  FoConsoleSlowRow,
  FoConsoleSlowSummary,
  FoConsoleUnattributedOrigin,
  FoConsoleUserView,
} from "@/lib/api";

/** The one "nothing to show" glyph, identical to the board's `EM_DASH`. */
export const EM_DASH = "—";

/* ------------------------------------------------------------------------ *
 * Contract v3 — Entra origin and the browser's own telemetry on a record
 * ------------------------------------------------------------------------ */

/**
 * The browser-RUM block of a record — the v1 shape plus the schema v2 columns
 * (endpoint network breakdown, H3/cache shares, Event Timing, long animation
 * frames, CPU/memory, JS error counts, the probe, the join kind). Every v2
 * field is optional: a record written by extension v0.1 has none of them, and
 * the panels render that as "not measured", never as zero.
 */
export type FoConsoleRumBlock = FoConsoleRumBlockV2;

/** One 15-minute RUM bucket on a person's timeline. */
export interface FoConsoleRumPoint extends FoConsoleRumBlock {
  bucket: string;
}

/** Where Entra places the person — the only location an unattributed row has. */
export interface RowOrigin {
  country: string;
  office: string;
}

/** The row's Entra country/office, or `null` when the identity map carries neither. */
export function rowOrigin(
  row: Pick<FoConsoleSlowRow, "country" | "office_location">,
): RowOrigin | null {
  const country = (row.country ?? "").trim();
  const office = (row.office_location ?? "").trim();
  if (country === "" && office === "") {
    return null;
  }
  return { country, office };
}

/** "DK · DISA"; one half alone when the other is blank. */
export function formatOrigin(origin: RowOrigin | null | undefined): string {
  if (!origin) {
    return EM_DASH;
  }
  return [origin.country, origin.office].filter((part) => part !== "").join(" · ");
}

/** The row's browser-RUM block, or `null` when no browser reported for this bucket. */
export function rowRum(row: Pick<FoConsoleSlowRow, "rum">): FoConsoleRumBlock | null {
  return row.rum ?? null;
}

/** The summary's unattributed origins; `null` when the payload predates contract v3. */
/**
 * Three states, kept apart: `undefined` — the payload predates contract v3 and
 * has no field; `null` — the field is there but the origins query did not
 * answer (unknown, not zero); an array — the query answered, possibly empty.
 */
export function summaryUnattributedOrigins(
  summary: Partial<Pick<FoConsoleSlowSummary, "unattributed_origins">>,
): FoConsoleUnattributedOrigin[] | null | undefined {
  return summary.unattributed_origins;
}

const BUCKET_MS = 15 * 60_000;

/** The 15-minute bucket a timestamp falls in, as an ISO string — the timeline's grouping of RUM points. */
export function rumBucket(ts: string): string | null {
  const parsed = Date.parse(ts);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return new Date(Math.floor(parsed / BUCKET_MS) * BUCKET_MS).toISOString();
}

/**
 * The person's RUM buckets for the timeline. The per-user view carries no RUM
 * series of its own (the function joins RUM onto slow rows only), so the points
 * are the distinct browser blocks attached to this person's slow interactions —
 * a sparse series, present exactly where a record exists, and captioned as such.
 */
export function viewRumPoints(view: Pick<FoConsoleUserView, "slow">): FoConsoleRumPoint[] {
  const byBucket = new Map<string, FoConsoleRumPoint>();
  for (const row of view.slow ?? []) {
    const rum = rowRum(row);
    const bucket = rumBucket(row.ts);
    if (rum && bucket && !byBucket.has(bucket)) {
      byBucket.set(bucket, { ...rum, bucket });
    }
  }
  return [...byBucket.values()].sort((a, b) => a.bucket.localeCompare(b.bucket));
}

/* ------------------------------------------------------------------------ *
 * Lookback
 * ------------------------------------------------------------------------ */

export interface LookbackOption {
  id: FoConsoleLookback;
  /** Chip label. */
  label: string;
  /** Caption form — "last 24 hours". */
  caption: string;
  /** Window length in milliseconds, for the timeline's x domain. */
  ms: number;
}

const HOUR_MS = 3_600_000;

export const LOOKBACKS: readonly LookbackOption[] = [
  { id: "1h", label: "1 h", caption: "last hour", ms: HOUR_MS },
  { id: "6h", label: "6 h", caption: "last 6 hours", ms: 6 * HOUR_MS },
  { id: "24h", label: "24 h", caption: "last 24 hours", ms: 24 * HOUR_MS },
  { id: "3d", label: "3 d", caption: "last 3 days", ms: 72 * HOUR_MS },
  { id: "7d", label: "7 d", caption: "last 7 days", ms: 168 * HOUR_MS },
];

export const DEFAULT_LOOKBACK: FoConsoleLookback = "24h";

/**
 * Narrow an arbitrary URL value to a lookback, falling back to `fallback`.
 *
 * The fallback is a parameter rather than a constant because one screen has a
 * different honest default: `/dynamics/pinpoint` ranks a *cumulative* cost
 * (person-hours of waiting), and the route it reads is served for a week by
 * default, so a bare link there opens on 7 d. The param itself is still the one
 * shared `?lookback=`, so a drill from the board carries the board's window and
 * the chips never disagree with the data.
 */
export function parseLookback(
  value: string | null | undefined,
  fallback: FoConsoleLookback = DEFAULT_LOOKBACK,
): FoConsoleLookback {
  const match = LOOKBACKS.find((option) => option.id === value);
  return match ? match.id : fallback;
}

export function lookbackOption(id: FoConsoleLookback): LookbackOption {
  return LOOKBACKS.find((option) => option.id === id) ?? (LOOKBACKS[2] as LookbackOption);
}

/**
 * The timeline's x domain: the lookback ending at `collected_at` (the instant
 * the workspace was read), or at `now` when nothing has been collected.
 */
export function timelineDomain(
  lookback: FoConsoleLookback,
  collectedAt: string | null | undefined,
  now: number = Date.now(),
): [number, number] {
  const end = collectedAt ? Date.parse(collectedAt) : Number.NaN;
  const safeEnd = Number.isFinite(end) ? end : now;
  return [safeEnd - lookbackOption(lookback).ms, safeEnd];
}

/* ------------------------------------------------------------------------ *
 * Classification — the §6 families, derived from the string prefix
 * ------------------------------------------------------------------------ */

/**
 * The classification families, in the fixed order every chart and legend uses.
 * `OTHER` folds any prefix the backend adds later (no enum mirrors live data).
 */
export const CLASSIFICATION_FAMILIES = [
  "APPLICATION",
  "SERVER",
  "NETWORK",
  "ENDPOINT",
  "BROWSER",
  "UNATTRIBUTED",
  "UNCLASSIFIED",
  "OTHER",
] as const;

export type ClassificationFamily = (typeof CLASSIFICATION_FAMILIES)[number];

/** Short tone vocabulary shared by `Badge` and the chips (no DS import needed here). */
export type ChipTone = "neutral" | "accent" | "warning" | "info";

/** Family from the backend's classification string, by prefix (`ENDPOINT/SESSION` → `ENDPOINT`). */
export function classificationFamily(
  classification: string | null | undefined,
): ClassificationFamily {
  const upper = (classification ?? "").trim().toUpperCase();
  if (upper.startsWith("APPLICATION")) {
    return "APPLICATION";
  }
  // Function v6. SERVER is narrower than APPLICATION on purpose: it is the one
  // verdict backed by a number F&O produced about itself (`Server-Timing`)
  // rather than inferred from a wall clock, so it must not be folded in with
  // findings that were reasoned to.
  if (upper.startsWith("SERVER")) {
    return "SERVER";
  }
  if (upper.startsWith("BROWSER")) {
    return "BROWSER";
  }
  if (upper.startsWith("NETWORK")) {
    return "NETWORK";
  }
  if (upper.startsWith("ENDPOINT")) {
    return "ENDPOINT";
  }
  if (upper.startsWith("UNATTRIBUTED")) {
    return "UNATTRIBUTED";
  }
  if (upper.startsWith("UNCLASSIFIED")) {
    return "UNCLASSIFIED";
  }
  return "OTHER";
}

export const CLASSIFICATION_LABEL: Record<ClassificationFamily, string> = {
  APPLICATION: "Application",
  SERVER: "Server (F&O's own timing)",
  NETWORK: "Network",
  ENDPOINT: "Endpoint / session",
  BROWSER: "Browser (tab throttled)",
  UNATTRIBUTED: "Unattributed",
  UNCLASSIFIED: "Unclassified",
  OTHER: "Other",
};

/**
 * Chip tone per family. These are causes, not severities, so none wears
 * success or danger: APPLICATION is HSO's problem (warning — needs a case),
 * SERVER is the same problem with proof attached and so wears it too, NETWORK
 * is a path finding (info), ENDPOINT points at one device (accent), BROWSER
 * says the browser slowed the tab deliberately and is therefore an explanation
 * rather than a fault (neutral), and the two "we do not know" families stay
 * neutral so a coverage gap never looks like a verdict.
 */
export const CLASSIFICATION_TONE: Record<ClassificationFamily, ChipTone> = {
  APPLICATION: "warning",
  SERVER: "warning",
  NETWORK: "info",
  ENDPOINT: "accent",
  BROWSER: "neutral",
  UNATTRIBUTED: "neutral",
  UNCLASSIFIED: "neutral",
  OTHER: "neutral",
};

export function classificationTone(classification: string | null | undefined): ChipTone {
  return CLASSIFICATION_TONE[classificationFamily(classification)];
}

/** The detail after the family — "form/data (peers slow in the same window)". */
export function classificationDetail(classification: string | null | undefined): string | null {
  const text = (classification ?? "").trim();
  const separator = text.search(/\s[-–—]\s/);
  if (separator < 0) {
    return null;
  }
  const detail = text
    .slice(separator)
    .replace(/^\s[-–—]\s/, "")
    .trim();
  return detail === "" ? null : detail;
}

/** The family word(s) before the separator — "APPLICATION (likely)", "NETWORK/PATH (browser-measured)". */
export function classificationHead(classification: string | null | undefined): string {
  const text = (classification ?? "").trim();
  const separator = text.search(/\s[-–—]\s/);
  return (separator < 0 ? text : text.slice(0, separator)).trim();
}

/**
 * The three UNATTRIBUTED sub-labels the function v3 emits, each with the exact
 * words the chip shows. Matched by substring so a rewording of the tail does
 * not silently fold a sub-label into another one.
 */
export const UNATTRIBUTED_SUBLABELS = {
  peers_normal: "peers normal — likely this user's path or device",
  too_few_peers: "too few peers",
  not_visible: "network layer not visible",
} as const;

export type UnattributedKind = keyof typeof UNATTRIBUTED_SUBLABELS;

/** Which UNATTRIBUTED sub-label a classification carries; `not_visible` when the tail is bare. */
export function unattributedKind(classification: string | null | undefined): UnattributedKind {
  const lower = (classificationDetail(classification) ?? "").toLowerCase();
  if (lower.includes("peers normal")) {
    return "peers_normal";
  }
  if (lower.includes("too few peers")) {
    return "too_few_peers";
  }
  return "not_visible";
}

/** How one classification string renders as a chip. */
export interface ClassificationPresentation {
  family: ClassificationFamily;
  tone: ChipTone;
  /** `outline` for a hedged label ("(likely)"), for UNATTRIBUTED, and for the unknown fold. */
  variant: "subtle" | "outline";
  /** The words on the chip. */
  label: string;
  /** The detail after the separator, when it is not already on the chip. */
  detail: string | null;
  /** `true` for "APPLICATION (likely)" — the small-cohort verdict. */
  hedged: boolean;
}

/** "NETWORK/PATH (BROWSER-MEASURED)" → "Network / path (browser-measured)". */
function humaniseHead(head: string): string {
  const lower = head.toLowerCase().replace(/\s*\/\s*/g, " / ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Derive the chip from the label grammar rather than from an enum of labels:
 * the family comes from the prefix, the variant from the hedge and the
 * family, the words from the head. An unknown string never becomes an empty
 * or family-less chip — it renders its own raw text on the unclassified tone.
 */
export function presentClassification(
  classification: string | null | undefined,
): ClassificationPresentation {
  const text = (classification ?? "").trim();
  const family = classificationFamily(text);
  const tone = CLASSIFICATION_TONE[family];
  const head = classificationHead(text);
  const detail = classificationDetail(text);
  const hedged = /\(likely\)/i.test(head);

  if (family === "OTHER") {
    return {
      family,
      tone,
      variant: "outline",
      label: text === "" ? CLASSIFICATION_LABEL.UNCLASSIFIED : text,
      detail: null,
      hedged: false,
    };
  }
  if (family === "UNATTRIBUTED") {
    return {
      family,
      tone,
      variant: "outline",
      label: `${CLASSIFICATION_LABEL.UNATTRIBUTED} · ${UNATTRIBUTED_SUBLABELS[unattributedKind(text)]}`,
      detail: null,
      hedged: false,
    };
  }
  return {
    family,
    tone,
    variant: hedged ? "outline" : "subtle",
    label: head === "" ? CLASSIFICATION_LABEL[family] : humaniseHead(head),
    detail,
    hedged,
  };
}

/* ------------------------------------------------------------------------ *
 * Function v4 — the browser tiers and the join kind
 * ------------------------------------------------------------------------ */

/**
 * The tiers the saved query inserts before ENDPOINT/SESSION, each derived from
 * the browser's own telemetry: four from function v4's schema-v2 measures, and
 * two more that function **v5** adds off the schema-v3 host signals (the laptop
 * throttling itself, and the tab running into its JS heap ceiling). They are
 * causes like every other classification (family by prefix, tone by family),
 * but the drawer names the *evidence* per tier, so the kind is derived here
 * from the label's tail and again from the numbers (`rumTierFromBlock`) — the
 * two must agree, and a test holds them to it.
 *
 * The array order is this module's derivation precedence, not a ranking. The v5
 * pair sits behind `device_load` because it is the same question asked with a
 * different instrument, and ahead of `browser_errors` and `probe_path` because
 * a machine that is throttling or a tab that is out of heap explains its own
 * slowness without reaching for the application or the path. The **workspace
 * function stays the authority for the label on a row**: this derivation exists
 * so the drawer can name evidence, never to overrule what the query said.
 */
export const RUM_TIERS = [
  "loaf",
  "device_load",
  "throttled",
  "heap_pressure",
  "browser_errors",
  "probe_path",
] as const;

export type RumTierKind = (typeof RUM_TIERS)[number];

/**
 * The function's labels, verbatim, so the chip and the reason text cannot drift
 * from the query. The last two are new in v5; a workspace still serving v4
 * simply never emits them.
 */
export const RUM_TIER_LABEL: Record<RumTierKind, string> = {
  loaf: "ENDPOINT - browser rendering (long animation frames)",
  device_load: "ENDPOINT - device under load",
  throttled: "ENDPOINT - device throttled (battery power saver)",
  heap_pressure: "ENDPOINT - browser tab near its memory limit",
  browser_errors: "APPLICATION - errors seen in the browser",
  probe_path: "NETWORK/PATH (probe) - endpoint path slow",
};

/** Thresholds of the tiers, shared with the backend function body. */
export const RUM_TIER_THRESHOLDS = {
  /** LoAF max ≥ this share of the F&O duration → rendering tier. */
  loafShare: 0.3,
  cpuPct: 85,
  memUsedPct: 90,
  probeTtfbMs: 1000,
  /**
   * Heap in use ≥ this percentage of the tab's ceiling → the memory tier, and
   * the same number that turns the drawer's heap line to a warning tone. One
   * constant so the sentence and the colour cannot disagree.
   */
  heapPct: 85,
} as const;

/** Which tier a classification string carries, or `null` for every other label. */
export function rumTierKind(classification: string | null | undefined): RumTierKind | null {
  const lower = (classification ?? "").toLowerCase();
  if (lower.includes("long animation frames")) {
    return "loaf";
  }
  if (lower.includes("device under load")) {
    return "device_load";
  }
  if (lower.includes("device throttled")) {
    return "throttled";
  }
  if (lower.includes("near its memory limit")) {
    return "heap_pressure";
  }
  if (lower.includes("errors seen in the browser")) {
    return "browser_errors";
  }
  if (lower.includes("(probe)")) {
    return "probe_path";
  }
  return null;
}

/** Chip tone per tier — the family's tone, since a tier is a cause within a family. */
export function rumTierTone(kind: RumTierKind): ChipTone {
  return CLASSIFICATION_TONE[classificationFamily(RUM_TIER_LABEL[kind])];
}

/** One clause per tier: what the browser measured that named it. */
export const RUM_TIER_EVIDENCE: Record<RumTierKind, string> = {
  loaf: "the longest animation frame covered a third or more of the form's duration — rendering on this device, not F&O",
  device_load:
    "CPU at or above 85 % or memory at or above 90 % while the form loaded — the device, not the path",
  throttled:
    "the laptop was on battery with a power-saving mode on, so the CPU was clocked down by the machine itself — a slower device, not a slower F&O",
  heap_pressure:
    "the tab's JS heap was at or above 85 % of the ceiling the browser allows it, where the collector starts costing the main thread real time — the browser tab, not the server",
  browser_errors:
    "the browser saw a 5xx or a JavaScript error in that minute — application side, the hash names the template",
  probe_path:
    "the idle probe to the app host was slow while Cato's path looked normal — the endpoint's own path",
};

/**
 * The tier the numbers themselves support, in the precedence `RUM_TIERS`
 * declares (LoAF → device → throttled → heap → errors → probe), or `null` when
 * none crosses its threshold. `catoNormal` is whether the Cato layer is visible
 * and inside its baseline — the probe tier only fires against a normal Cato path
 * (or no Cato at all), exactly as the function has it.
 *
 * The two v5 tiers read schema-v3 host signals, so they are unreachable from a
 * minute that carries none: `on_battery`/`power_saver` are `null` rather than
 * `false` when the browser could not say, and `null` never crosses a threshold.
 * That is the whole reason they are compared explicitly against `true` and
 * `!= null` — a `!rum.on_battery` test would read "the machine did not say" as
 * "the machine was plugged in".
 */
export function rumTierFromBlock(
  rum: Pick<
    FoConsoleRumBlock,
    | "loaf_max_ms"
    | "cpu_pct"
    | "mem_used_pct"
    | "status_5xx"
    | "js_errors"
    | "probe_ttfb_ms"
    | "on_battery"
    | "power_saver"
    | "heap_used_mb"
    | "heap_limit_mb"
  > | null,
  durationMs: number,
  catoNormal: boolean,
): RumTierKind | null {
  if (!rum) {
    return null;
  }
  if (
    rum.loaf_max_ms != null &&
    durationMs > 0 &&
    rum.loaf_max_ms >= RUM_TIER_THRESHOLDS.loafShare * durationMs
  ) {
    return "loaf";
  }
  if (
    (rum.cpu_pct != null && rum.cpu_pct >= RUM_TIER_THRESHOLDS.cpuPct) ||
    (rum.mem_used_pct != null && rum.mem_used_pct >= RUM_TIER_THRESHOLDS.memUsedPct)
  ) {
    return "device_load";
  }
  // Both flags, not either: a desk machine in power-saving mode is not
  // throttled the way a laptop on battery is, and a laptop on battery with
  // power saving off is running at full clock.
  if (rum.on_battery === true && rum.power_saver === true) {
    return "throttled";
  }
  if (heapIsTight(rum.heap_used_mb, rum.heap_limit_mb)) {
    return "heap_pressure";
  }
  if ((rum.status_5xx ?? 0) > 0 || (rum.js_errors ?? 0) > 0) {
    return "browser_errors";
  }
  if (
    rum.probe_ttfb_ms != null &&
    rum.probe_ttfb_ms > RUM_TIER_THRESHOLDS.probeTtfbMs &&
    catoNormal
  ) {
    return "probe_path";
  }
  return null;
}

/** The join kind a block carries; a v1 block (no field) joined by Mail × bucket, as v3 did. */
export function rumJoinKind(
  rum: Pick<FoConsoleRumBlock, "join_kind"> | null | undefined,
): FoConsoleRumJoinKind {
  if (!rum) {
    return "none";
  }
  const kind = rum.join_kind;
  return kind === "activity" || kind === "bucket" ? kind : "bucket";
}

/** The words on the join-kind chip. */
export const JOIN_KIND_LABEL: Record<FoConsoleRumJoinKind, string> = {
  activity: "matched by activity id",
  bucket: "same 15 min, same user",
  none: "no browser minute joined",
};

/** The caption under the chip — how much to trust the browser block for *this* activity. */
export const JOIN_KIND_DETAIL: Record<FoConsoleRumJoinKind, string> = {
  activity:
    "The browser saw this very request id in that minute, so the block below is this interaction's own telemetry.",
  bucket:
    "No request id matched; the block is the same person's browser minute nearest the interaction — it may hold a neighbouring request.",
  none: "No browser minute could be joined to this interaction.",
};

/** Chip tone: an exact match is a finding, the fallback stays neutral. */
export const JOIN_KIND_TONE: Record<FoConsoleRumJoinKind, ChipTone> = {
  activity: "accent",
  bucket: "neutral",
  none: "neutral",
};

/** A 0..1 share as a percentage with no decimals — "71 %"; an em dash when absent. */
export function formatShare(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return `${Math.round(Math.min(1, Math.max(0, value)) * 100)} %`;
}

/** A 0..100 reading as "83 %"; an em dash when absent. */
export function formatPctWhole(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return `${Math.round(value)} %`;
}

/** Whether a block carries any schema v2 measurement at all (an extension v0.2 minute). */
export function hasRumV2(rum: FoConsoleRumBlock | null | undefined): boolean {
  if (!rum) {
    return false;
  }
  return (
    rum.dns_p50_ms != null ||
    rum.tls_p50_ms != null ||
    rum.connect_p50_ms != null ||
    rum.h3_share != null ||
    rum.cache_hit_share != null ||
    rum.interaction_p50_ms != null ||
    rum.loaf_max_ms != null ||
    rum.cpu_pct != null ||
    rum.mem_used_pct != null ||
    rum.js_errors != null ||
    rum.status_5xx != null ||
    rum.probe_ttfb_ms != null
  );
}

/* ------------------------------------------------------------------------ *
 * Schema v3 — the machine the browser was running on
 * ------------------------------------------------------------------------ */

/**
 * What a machine signal reads as when the browser did not report it.
 *
 * Deliberately **not** `EM_DASH` and deliberately not "unknown device". These
 * fields arrive `null` for two very different reasons — the minute predates
 * extension 0.3, or the browser refused the API on that platform — and in both
 * cases the honest reading is that nobody said, which is what these words mean.
 * "0 GB", "no battery" or "unknown device" would each invent a machine.
 */
export const NOT_REPORTED = "not reported";

/** Installed memory as `8 GB`, trailing zeros dropped; `not reported` for null. */
export function formatMachineGb(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value < 0) {
    return NOT_REPORTED;
  }
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} GB`;
}

/** A heap figure as `1,850 MB`; `not reported` for null. */
export function formatMachineMb(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value < 0) {
    return NOT_REPORTED;
  }
  return `${Math.round(value).toLocaleString("en-GB")} MB`;
}

/**
 * Heap in use as a percentage of the tab's ceiling, or `null`.
 *
 * `null` whenever either side is missing or the ceiling is zero — a share
 * against an unknown denominator is not a share, and "0 %" of an unknown limit
 * would read as a tab with room to spare.
 */
export function heapPct(
  used: number | null | undefined,
  limit: number | null | undefined,
): number | null {
  if (used == null || limit == null || !Number.isFinite(used) || !Number.isFinite(limit)) {
    return null;
  }
  if (limit <= 0) {
    return null;
  }
  return (used / limit) * 100;
}

/**
 * Whether the tab is at or above `RUM_TIER_THRESHOLDS.heapPct` of its ceiling.
 *
 * `false` when the share is unknown, which is the safe direction: an unknown
 * heap must not paint a warning, because a warning nobody can act on is noise.
 */
export function heapIsTight(
  used: number | null | undefined,
  limit: number | null | undefined,
): boolean {
  const pct = heapPct(used, limit);
  return pct != null && pct >= RUM_TIER_THRESHOLDS.heapPct;
}

/** The function's own short device label, or the "nobody said" words. */
export function deviceClassText(rum: Pick<FoConsoleRumBlock, "device_class"> | null | undefined) {
  const label = (rum?.device_class ?? "").trim();
  return label === "" ? NOT_REPORTED : label;
}

/**
 * Power state in one clause — "on battery · 41 % charge · power saving (derived)".
 *
 * Built from the parts that exist rather than from a template with holes: a
 * machine that reported a charge level but not whether it was on mains says
 * "38 % charge" and nothing more. `power_saver: false` is left out on purpose —
 * "plugged in" already carries it, and a line that says what is *not* happening
 * costs density for no information.
 */
export function batteryText(
  rum: Pick<FoConsoleRumBlock, "on_battery" | "battery_pct" | "power_saver"> | null | undefined,
): string {
  if (!rum) {
    return NOT_REPORTED;
  }
  const parts: string[] = [];
  if (rum.on_battery === true) {
    parts.push("on battery");
  } else if (rum.on_battery === false) {
    parts.push("plugged in");
  }
  if (rum.battery_pct != null && Number.isFinite(rum.battery_pct)) {
    parts.push(`${Math.round(rum.battery_pct)} % charge`);
  }
  if (rum.power_saver === true) {
    // "(derived)" because no browser exposes the OS setting: it is inferred
    // from the platform's own hints, so the word says how sure this is.
    parts.push("power saving (derived)");
  }
  return parts.length === 0 ? NOT_REPORTED : parts.join(" · ");
}

/** The tab's heap as `used / limit (pct)`; the parts that exist, never a fabricated ratio. */
export function heapText(
  rum: Pick<FoConsoleRumBlock, "heap_used_mb" | "heap_limit_mb"> | null | undefined,
): string {
  if (!rum || (rum.heap_used_mb == null && rum.heap_limit_mb == null)) {
    return NOT_REPORTED;
  }
  const pct = heapPct(rum.heap_used_mb, rum.heap_limit_mb);
  const base = `${formatMachineMb(rum.heap_used_mb)} / ${formatMachineMb(rum.heap_limit_mb)}`;
  return pct == null ? base : `${base} (${Math.round(pct)} %)`;
}

/** One line of the machine line-up. */
export interface MachineLine {
  label: string;
  /** Already formatted — `NOT_REPORTED` when the browser said nothing. */
  value: string;
  /** `true` only for a reading that has crossed a threshold worth a warning tone. */
  warn: boolean;
}

/**
 * The machine the browser was running on, as a line-up for the record drawer.
 *
 * Six lines, always six, in a fixed order — because a line-up that hides its
 * empty rows makes a v0.2 minute look like a machine that simply had nothing
 * remarkable to report. Every value is a string, so the drawer has no null
 * handling of its own to get wrong, and the only tone in the whole line-up is
 * the heap warning: none of these is a severity, and a slow laptop is a fact
 * about the endpoint, not a fault.
 */
export function machineLineUp(rum: FoConsoleRumBlock | null | undefined): MachineLine[] {
  const cpu = (rum?.cpu_model ?? "").trim();
  const gpu = (rum?.gpu_renderer ?? "").trim();
  const idle = (rum?.idle_state ?? "").trim();
  return [
    { label: "CPU", value: cpu === "" ? NOT_REPORTED : cpu, warn: false },
    { label: "Installed memory", value: formatMachineGb(rum?.mem_total_gb), warn: false },
    { label: "Power", value: batteryText(rum), warn: false },
    {
      label: "Tab heap (used / limit)",
      value: heapText(rum),
      warn: heapIsTight(rum?.heap_used_mb, rum?.heap_limit_mb),
    },
    { label: "Graphics adapter", value: gpu === "" ? NOT_REPORTED : gpu, warn: false },
    { label: "Screen state", value: idle === "" ? NOT_REPORTED : idle, warn: false },
  ];
}

/**
 * Whether this minute carries any schema v3 host signal at all.
 *
 * The counterpart of `hasRumV2`: false means the line-up above is six rows of
 * "not reported" and the drawer says which version is why, rather than leaving
 * the reader to wonder whether the machine is a mystery or the minute is old.
 */
export function hasRumV3(rum: FoConsoleRumBlock | null | undefined): boolean {
  if (!rum) {
    return false;
  }
  return (
    rum.cpu_model != null ||
    rum.mem_total_gb != null ||
    rum.on_battery != null ||
    rum.power_saver != null ||
    rum.battery_pct != null ||
    rum.heap_used_mb != null ||
    rum.heap_limit_mb != null ||
    rum.gpu_renderer != null ||
    rum.idle_state != null ||
    rum.device_class != null
  );
}

/* ------------------------------------------------------------------------ *
 * Device facts — `/fo/console/device`, one Defender hunting query
 * ------------------------------------------------------------------------ */

/** The permission the hunting query needs, named in every refusal. */
export const DEVICE_PERMISSION = "ThreatHunting.Read.All";

/** How far back the hunting query looks — the backend's `DEVICE_LOOKBACK_DAYS`. */
export const DEVICE_LOOKBACK_DAYS = 14;

/** The words for an `ok` answer that found no machine. Not an error, and not "no devices". */
export const DEVICE_EMPTY_LINE =
  `Defender has no device for this person in the last ${DEVICE_LOOKBACK_DAYS} days. ` +
  "That is a real answer, not a failure: a machine nobody onboarded, or one signed into " +
  "only through a browser, leaves no `DeviceInfo` row. It does not mean they have no computer.";

/** The states `/fo/console/device` can answer with — the payload's own union. */
export type FoConsoleDeviceAccessState = FoConsoleDeviceFacts["access_state"];

export interface DeviceStateCopy {
  /** Section heading for the state. */
  title: string;
  /** `Alert` tone. Nothing here is `danger` unless NOVA is actually broken. */
  tone: "warning" | "danger" | "neutral";
  /** The one sentence NOVA adds to the backend's own `status_detail`. */
  line: string;
}

/**
 * What each non-`ok` device state means, in one sentence each.
 *
 * `unauthorised` is the **expected** first answer in production, because
 * `ThreatHunting.Read.All` is not consented on NOVA's Graph credential yet. So
 * it is information, not a failure: it names the permission, says a tenant admin
 * has to consent it, and carries no retry — retrying an un-granted permission
 * just writes another audit row for the same refusal. Its tone is `warning`
 * rather than `danger` for the same reason: nothing is broken, a role is
 * missing. The backend's `status_detail` is rendered verbatim beside these
 * words and already names the permission and who can grant it.
 */
export const DEVICE_STATE_COPY: Record<FoConsoleDeviceAccessState, DeviceStateCopy> = {
  ok: {
    title: "Reading Defender",
    tone: "neutral",
    line: "Defender answered the hunting query.",
  },
  no_credential: {
    title: "Defender was never asked",
    tone: "warning",
    line:
      "NOVA holds no Graph credential for Defender, so no lookup was made at all — set " +
      "GRAPH_APP_CLIENT_ID and GRAPH_APP_CLIENT_SECRET on the nova-api Container App, or give it " +
      "a managed identity. Until then the machines behind this person are unknown, not absent.",
  },
  unauthorised: {
    title: `Defender refused the lookup — ${DEVICE_PERMISSION} is not granted`,
    tone: "warning",
    line:
      `Defender accepted NOVA's credential and then refused the query: the application permission ` +
      `${DEVICE_PERMISSION} is not consented, and only a tenant administrator can grant and ` +
      "consent it. Nothing is retried here, because retrying a permission that is not granted " +
      "only writes another audit row for the same refusal.",
  },
  workspace_not_found: {
    title: "Defender is not provisioned for this tenant",
    tone: "danger",
    line:
      "The advanced-hunting endpoint answered that there is no Defender workspace to query. " +
      "That is a tenant-provisioning fact, not a NOVA configuration value someone can correct.",
  },
  query_rejected: {
    title: "Defender rejected the hunting query",
    tone: "danger",
    line:
      "The credential and the workspace are both fine and the query itself was refused — schema " +
      "drift on `DeviceInfo`, or a NOVA-side bug. This one is a code fix, not a role assignment.",
  },
  unavailable: {
    title: "Defender did not answer",
    tone: "warning",
    line:
      "The endpoint was reachable but the request did not complete. Transient by nature: reopening " +
      "this section asks again. Nothing is shown as zero in the meantime.",
  },
};

/** One machine, formatted for a compact row. Every field may be blank on the wire. */
export interface DeviceRow {
  /** Defender's device name, or the words for a row that carries none. */
  name: string;
  model: string;
  /** `Windows11 10.0.22631 · build 22631` — the parts that exist. */
  os: string;
  type: string;
  joined: string;
  onboarding: string;
  /** Absolute UTC-ish timestamp, or `not reported`. */
  lastSeen: string;
  /** The raw `last_seen`, for the relative-age label the row carries beside it. */
  lastSeenAt: string | null;
  /** Installed memory. Always `not reported` from Defender — see the note below. */
  memory: string;
}

/**
 * One `FoConsoleDevice` as display strings.
 *
 * Defender's `DeviceInfo` fields arrive as **empty strings**, not nulls, when it
 * has nothing (the model defaults them to `""`), so every one of them is folded
 * to `NOT_REPORTED` here — an empty table cell reads as a machine with no model,
 * which is a different claim from a machine Defender did not describe.
 *
 * `ram_gb` is null by design and stays null: `DeviceInfo` has no memory column
 * at all, so the only measured figure NOVA has is the browser's own
 * `mem_total_gb` on the RUM block. The row keeps the line rather than dropping
 * it, because "not reported" is the answer to "how much RAM does this machine
 * have?" and a missing line invites someone to go looking for a number that is
 * not there. There is deliberately no compliance field: that lives on Intune's
 * `managedDevice`, which NOVA cannot read.
 */
export function deviceRow(device: FoConsoleDevice): DeviceRow {
  const text = (value: string | null | undefined) => {
    const trimmed = (value ?? "").trim();
    return trimmed === "" ? NOT_REPORTED : trimmed;
  };
  const osParts = [device.os_platform, device.os_version]
    .map((part) => (part ?? "").trim())
    .filter((part) => part !== "");
  const build = (device.os_build ?? "").trim();
  const os = osParts.length === 0 ? NOT_REPORTED : osParts.join(" ");
  return {
    name: text(device.device_name),
    model: text(device.model),
    os: build === "" ? os : `${os} · build ${build}`,
    type: text(device.device_type),
    joined: text(device.joined),
    onboarding: text(device.onboarding_status),
    lastSeen: device.last_seen == null ? NOT_REPORTED : device.last_seen,
    lastSeenAt: device.last_seen,
    memory: formatMachineGb(device.ram_gb),
  };
}

/** Vendor and model on one line — "Dell Inc. Latitude 7420"; the half that exists, alone. */
export function deviceModelText(device: FoConsoleDevice): string {
  const parts = [device.vendor, device.model]
    .map((part) => (part ?? "").trim())
    .filter((part) => part !== "");
  return parts.length === 0 ? NOT_REPORTED : parts.join(" ");
}

/* ------------------------------------------------------------------------ *
 * User reports — "this app is slow right now"
 * ------------------------------------------------------------------------ */

/** The triage states a report moves through, in queue order. */
export const REPORT_STATUSES = ["new", "triaged", "closed"] as const;

/** The words on a status chip. */
export const REPORT_STATUS_LABEL: Record<FoConsoleReportStatus, string> = {
  new: "new",
  triaged: "triaged",
  closed: "closed",
};

/** What each state means to whoever picks the queue up next. */
export const REPORT_STATUS_DETAIL: Record<FoConsoleReportStatus, string> = {
  new: "nobody has looked at this yet",
  triaged: "looked at, cause named, work is elsewhere",
  closed: "no further action from the console",
};

/**
 * Chip tone per state. A report is a queue item, not a severity, so nothing
 * here wears danger: `new` is the one that wants attention (warning), `triaged`
 * is in hand (accent), `closed` is done (neutral).
 */
export const REPORT_STATUS_TONE: Record<FoConsoleReportStatus, ChipTone> = {
  new: "warning",
  triaged: "accent",
  closed: "neutral",
};

/** The status a URL carries, or `null` for "every status" — an unknown word is not a filter. */
export function parseReportStatus(value: string | null | undefined): FoConsoleReportStatus | null {
  const lower = (value ?? "").trim().toLowerCase();
  return REPORT_STATUSES.find((status) => status === lower) ?? null;
}

/**
 * Where a report may go from where it is. Deliberately not "anything to
 * anything": reopening a closed report lands in `triaged`, because it has
 * already been read once and calling it `new` again would tell the next
 * investigator a lie about the queue.
 */
export const REPORT_STATUS_TRANSITIONS: Record<
  FoConsoleReportStatus,
  readonly FoConsoleReportStatus[]
> = {
  new: ["triaged", "closed"],
  triaged: ["closed", "new"],
  closed: ["triaged"],
};

/** The states the status control offers for a report currently in `from`. */
export function reportStatusTransitions(
  from: FoConsoleReportStatus,
): readonly FoConsoleReportStatus[] {
  return REPORT_STATUS_TRANSITIONS[from];
}

/** Whether `to` is a move the control may offer. A no-op (`from === to`) is not a move. */
export function canTransitionReport(
  from: FoConsoleReportStatus,
  to: FoConsoleReportStatus,
): boolean {
  return from !== to && REPORT_STATUS_TRANSITIONS[from].includes(to);
}

/** One option of the report queue's window picker. */
export interface ReportLookbackOption {
  id: FoConsoleReportLookback;
  label: string;
  caption: string;
}

/**
 * The report store's own windows. These are **not** the console's lookbacks:
 * `FoSlowInteractions()` is served for five trailing KQL windows, while the
 * queue is a Postgres table purged at 90 days, so offering `1h` here would be a
 * control that hid the queue rather than scoping it.
 */
export const REPORT_LOOKBACKS: readonly ReportLookbackOption[] = [
  { id: "24h", label: "24 h", caption: "reports in the last 24 hours" },
  { id: "7d", label: "7 d", caption: "reports in the last 7 days" },
  { id: "30d", label: "30 d", caption: "reports in the last 30 days" },
  {
    id: "90d",
    label: "90 d",
    caption: "reports in the last 90 days — the store's whole retention",
  },
];

/** The queue's default window: a week, long enough that a quiet Monday is not an empty page. */
export const DEFAULT_REPORT_LOOKBACK: FoConsoleReportLookback = "7d";

/** The window a URL carries, defaulting to a week; an unknown word is not a window. */
export function parseReportLookback(value: string | null | undefined): FoConsoleReportLookback {
  const match = REPORT_LOOKBACKS.find((option) => option.id === value);
  return match ? match.id : DEFAULT_REPORT_LOOKBACK;
}

/** The option for an id — never undefined, so a caption is always available. */
export function reportLookbackOption(id: FoConsoleReportLookback): ReportLookbackOption {
  return REPORT_LOOKBACKS.find((option) => option.id === id) ?? REPORT_LOOKBACKS[1]!;
}

/**
 * How much to trust a report's `matches` list. `live` re-ran the match against
 * the workspace; `stored` is the match made at submit time because the report
 * is older than the workspace retention the re-run needs; `unavailable` means
 * the match could not be made at all — which is not "nothing matched".
 */
export const REPORT_MATCH_STATE_COPY: Record<string, string> = {
  live: "Matched live against the workspace just now.",
  stored:
    "The match was made when the report arrived; the report is now older than the window a re-run can read, so these are the stored ids, not a fresh query.",
  unavailable:
    "The match could not be made — the workspace did not answer. No F&O load is claimed either way.",
};

/** The sentence under a report's matched list; an unknown state says so rather than nothing. */
export function reportMatchStateDetail(state: string | null | undefined): string {
  const key = (state ?? "").trim().toLowerCase();
  return (
    REPORT_MATCH_STATE_COPY[key] ??
    "The backend did not say how this report was matched, so treat the ids below as unverified."
  );
}

/**
 * The words for a report that matched nothing. A report with no matched F&O
 * load is a real finding — the slowness the user felt was not a slow form load
 * — and must never read as a failed lookup.
 */
export const REPORT_NO_MATCH_LINE =
  "No F&O form load matched this report. What the user felt was not a slow form load in the workspace's eyes — check the browser block and the probe before closing it.";

/* ------------------------------------------------------------------------ *
 * Network state — the explicit "not visible" chip
 * ------------------------------------------------------------------------ */

/**
 * The network states the §10 query emits. The wire type is a plain string (no
 * enum mirrors live data, §8); anything that is not a known visible state folds
 * to `unattributed` — the honest fallback, since an unknown word must never
 * render as a clean path.
 */
export type FoConsoleNetState = "normal" | "degraded" | "unattributed";

export function parseNetState(value: string | null | undefined): FoConsoleNetState {
  const lower = (value ?? "").trim().toLowerCase();
  if (lower === "normal" || lower === "degraded") {
    return lower;
  }
  return "unattributed";
}

/** The exact text an unattributed row shows wherever a network value would sit. */
export const UNATTRIBUTED_CHIP = "network layer not visible for this user";

export const NET_STATE_LABEL: Record<FoConsoleNetState, string> = {
  normal: "inside site baseline",
  degraded: "above 7-day baseline",
  unattributed: UNATTRIBUTED_CHIP,
};

export const NET_STATE_TONE: Record<FoConsoleNetState, "success" | "warning" | "neutral"> = {
  normal: "success",
  degraded: "warning",
  unattributed: "neutral",
};

/** Whether a row's network block can be rendered as numbers at all. */
export function hasNetworkBlock(row: Pick<FoConsoleSlowRow, "net_state" | "site">): boolean {
  return parseNetState(row.net_state) !== "unattributed" && row.site != null && row.site !== "";
}

/* ------------------------------------------------------------------------ *
 * Formatters — `null` is an em dash, never `0`
 * ------------------------------------------------------------------------ */

/** A duration in the unit a human reads it in — `420 ms`, `6.2 s`, `41 s`. */
export function formatDurationMs(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return EM_DASH;
  }
  if (value < 1000) {
    return `${Math.round(value)} ms`;
  }
  const seconds = value / 1000;
  return `${seconds.toFixed(seconds >= 10 ? 0 : 1)} s`;
}

/** Compact duration for axis ticks — `0.5s`, `6s`, `41s`. */
export function formatDurationCompact(value: number): string {
  if (value < 1000) {
    return `${Math.round(value)}ms`;
  }
  const seconds = value / 1000;
  return `${seconds.toFixed(seconds >= 10 ? 0 : 1)}s`;
}

export function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return value.toLocaleString();
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return `${value.toFixed(1)}%`;
}

/* ------------------------------------------------------------------------ *
 * Coverage — beside every ratio
 * ------------------------------------------------------------------------ */

/** Coverage at or above this reads as healthy. */
export const COVERAGE_GOOD_PCT = 75;
/** Between this and {@link COVERAGE_GOOD_PCT} the ratio is usable but partial. */
export const COVERAGE_PARTIAL_PCT = 40;

export type CoverageTone = "success" | "warning" | "danger" | "neutral";

/** "146 of 185 (78.9%)"; "— (no slow interactions)" when there is no denominator. */
export function formatCoverage(coverage: FoConsoleCoverage | null | undefined): string {
  if (!coverage || coverage.total === 0 || coverage.pct == null) {
    return `${EM_DASH} (no slow interactions to cover)`;
  }
  return `${formatCount(coverage.with_network)} of ${formatCount(coverage.total)} (${formatPercent(
    coverage.pct,
  )})`;
}

/** Tone for a coverage meter — neutral when there is nothing to cover (never green). */
export function coverageTone(pct: number | null | undefined): CoverageTone {
  if (pct == null || !Number.isFinite(pct)) {
    return "neutral";
  }
  if (pct >= COVERAGE_GOOD_PCT) {
    return "success";
  }
  if (pct >= COVERAGE_PARTIAL_PCT) {
    return "warning";
  }
  return "danger";
}

/** The one caption every coverage meter carries — why the number is what it is. */
export const COVERAGE_CAPTION =
  "users Cato can see ÷ slow interactions — DKTAA/CZPRI are not behind Cato yet";

/* ------------------------------------------------------------------------ *
 * Browser RUM coverage — the second coverage on the board
 * ------------------------------------------------------------------------ */

/** The caption under the RUM meter — what the ratio is, in the same grammar as Cato's. */
export const RUM_COVERAGE_CAPTION =
  "browsers reporting ÷ active F&O users (24 h) — the fo-rum extension posts the browser's own timings";

/** "12 of 185 (6.5%)"; "— (no active F&O users to cover)" without a denominator. */
export function formatRumCoverage(coverage: FoConsoleRumCoverage | null | undefined): string {
  if (!coverage || coverage.total === 0 || coverage.pct == null) {
    return `${EM_DASH} (no active F&O users to cover)`;
  }
  return `${formatCount(coverage.with_rum)} of ${formatCount(coverage.total)} (${formatPercent(
    coverage.pct,
  )})`;
}

/**
 * The rollout states the RUM meter distinguishes. `not_rolled_out` is the one
 * that matters today: the ingestion is wired, nothing has posted — which must
 * read as "extension not rolled out yet", never as 0 % coverage of a working
 * source. `unknown` is different again: the workspace did not answer the
 * activity query (`users_24h: null`), so nothing can be said about the fleet —
 * not even "nobody posts".
 */
export type RumRolloutState =
  | "unconfigured"
  | "disabled"
  | "unknown"
  | "not_rolled_out"
  | "reporting";

export function rumRolloutState(
  status: Pick<FoConsoleRumStatus, "configured" | "enabled" | "users_24h"> | null | undefined,
): RumRolloutState {
  if (!status || !status.configured) {
    return "unconfigured";
  }
  if (!status.enabled) {
    return "disabled";
  }
  if (status.users_24h == null) {
    return "unknown";
  }
  if (status.users_24h === 0) {
    return "not_rolled_out";
  }
  return "reporting";
}

export const RUM_STATE_COPY: Record<RumRolloutState, { title: string; detail: string }> = {
  unconfigured: {
    title: "Browser telemetry not configured",
    detail:
      "NOVA has no ingestion stream for BrowserRUM_CL, so no browser can report. Rows without a Cato row stay unattributed.",
  },
  disabled: {
    title: "Browser telemetry disabled",
    detail:
      "The RUM intake is switched off in NOVA's settings; posts from the extension are refused.",
  },
  unknown: {
    title: "Browser-telemetry count unknown",
    detail:
      "The workspace did not answer the RUM activity query, so whether any browser reports is unknown — not zero. No statement about the fleet is made from this card until it answers.",
  },
  not_rolled_out: {
    title: "Extension not rolled out yet",
    detail:
      "No browser has posted F&O telemetry in the last 24 hours. Until the fo-rum extension reaches the first users, server wait, main-thread time and endpoint RTT are unknown — not zero — for every row.",
  },
  reporting: {
    title: "Browsers reporting",
    detail: "Coverage is the share of people active in F&O whose browser posted at least one row.",
  },
};

/* ------------------------------------------------------------------------ *
 * The reason gate (D7)
 * ------------------------------------------------------------------------ */

/** A reason is a ticket/report id or free text of at least this many characters. */
export const REASON_MIN_LENGTH = 4;

/** Where the session's reason lives — `sessionStorage`, so a new tab asks again. */
export const REASON_STORAGE_KEY = "nova.fo.reason";

/** The subset of `Storage` the gate needs, so tests can pass a plain object. */
export interface ReasonStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

export function normaliseReason(reason: string | null | undefined): string {
  return (reason ?? "").replace(/\s+/g, " ").trim();
}

export function isReasonValid(reason: string | null | undefined): boolean {
  return normaliseReason(reason).length >= REASON_MIN_LENGTH;
}

/** The stored reason, or `null` when absent, too short, or storage is unavailable. */
export function readStoredReason(storage: ReasonStorage | null | undefined): string | null {
  if (!storage) {
    return null;
  }
  try {
    const stored = normaliseReason(storage.getItem(REASON_STORAGE_KEY));
    return isReasonValid(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Persist a valid reason; an invalid one clears the slot rather than storing junk. */
export function writeStoredReason(
  storage: ReasonStorage | null | undefined,
  reason: string | null | undefined,
): string | null {
  const normalised = normaliseReason(reason);
  if (!storage) {
    return isReasonValid(normalised) ? normalised : null;
  }
  try {
    if (isReasonValid(normalised)) {
      storage.setItem(REASON_STORAGE_KEY, normalised);
      return normalised;
    }
    storage.removeItem(REASON_STORAGE_KEY);
    return null;
  } catch {
    return isReasonValid(normalised) ? normalised : null;
  }
}

/* ------------------------------------------------------------------------ *
 * Record reason text — derived client-side from the record's own fields
 * ------------------------------------------------------------------------ */

/** A numeric comparison is only made when both sides exist. */
function above(value: number | null, baseline: number | null, factor: number): boolean {
  return value != null && baseline != null && baseline > 0 && value > factor * baseline;
}

/**
 * The reason sentences for one §5 record, in evidence order: what F&O
 * measured against its baselines, what the peers did, what the network path
 * did (or that it is not visible), and what the exceptions say. Each sentence
 * names the numbers it rests on, so the reader can disagree with it.
 */
export function deriveRecordReasonParts(row: FoConsoleSlowRow): string[] {
  const parts: string[] = [];

  const versus: string[] = [];
  if (row.form_p50_ms != null || row.form_p95_ms != null) {
    versus.push(
      `form p50/p95 ${formatDurationMs(row.form_p50_ms)} / ${formatDurationMs(row.form_p95_ms)} (28 d, this entity)`,
    );
  }
  parts.push(
    versus.length > 0
      ? `F&O total ${formatDurationMs(row.duration_ms)} against ${versus.join(", ")}.`
      : `F&O total ${formatDurationMs(row.duration_ms)}; no 28-day baseline exists for this form and entity yet.`,
  );

  if (row.exceptions.length > 0) {
    const first = row.exceptions[0] ?? "";
    parts.push(
      `${formatCount(row.exceptions.length)} F&O exception${
        row.exceptions.length === 1 ? "" : "s"
      } fired in this activity (${first}) — error, retry or contention on the application side.`,
    );
  }

  const peerUsers = row.peer_users ?? 0;
  // Tier 2: a real cohort, clearly slow. Tier 2b: a small cohort (3–4 users,
  // or ≥5 between 1.5× and 2×) — "likely", never a verdict.
  const peersSlow = peerUsers >= 5 && above(row.peer_p50_ms, row.form_p50_ms, 2);
  const peersLikely =
    !peersSlow &&
    peerUsers >= 3 &&
    row.peer_p50_ms != null &&
    row.form_p50_ms != null &&
    row.form_p50_ms > 0 &&
    row.peer_p50_ms >= 1.5 * row.form_p50_ms;
  const peersNormal =
    peerUsers >= 3 &&
    row.peer_p50_ms != null &&
    row.form_p50_ms != null &&
    row.peer_p50_ms < 1.5 * row.form_p50_ms;
  if (peersSlow) {
    parts.push(
      `Peers were slow too: ${formatCount(row.peer_users)} other users ran ${row.form} at p50 ${formatDurationMs(
        row.peer_p50_ms,
      )} in the same 15 minutes — the form or its data, not this person.`,
    );
  } else if (peersLikely) {
    parts.push(
      `Peers were slow too, but only ${formatCount(row.peer_users)} of them: p50 ${formatDurationMs(
        row.peer_p50_ms,
      )} for ${row.form} in the same 15 minutes against the form's 28-day p50 of ${formatDurationMs(
        row.form_p50_ms,
      )} — likely the form or its data, small cohort.`,
    );
  } else if (row.peer_users != null && row.peer_p50_ms != null) {
    parts.push(
      `Peers in the same 15 minutes: ${formatCount(row.peer_users)} other users at p50 ${formatDurationMs(
        row.peer_p50_ms,
      )}${peersNormal ? " — normal for this form" : ""}.`,
    );
  } else {
    parts.push(
      "No other user ran this form in the same 15 minutes, so there is no peer comparison.",
    );
  }

  const netState = parseNetState(row.net_state);
  const networkVisible = netState !== "unattributed" && hasNetworkBlock(row);
  const rum = rowRum(row);
  const origin = rowOrigin(row);
  if (!networkVisible) {
    parts.push(
      `Network layer not visible for this user: no Cato row in this 15-minute bucket, so the path is unknown — a coverage gap, not a clean path.${
        origin ? ` Entra places them in ${formatOrigin(origin)}, outside Cato's view.` : ""
      } ${
        rum
          ? "Peer comparison and the browser's own telemetry are the evidence."
          : "Peer comparison is the only evidence."
      }`,
    );
  } else if (netState === "degraded") {
    parts.push(
      `Network path above its baseline at ${row.site}: TTFB ${formatDurationMs(row.ttfb_ms)} against the site's 7-day p50 of ${formatDurationMs(
        row.site_ttfb_p50_ms,
      )}, TLS ${formatDurationMs(row.tls_ms)}, TCP ${formatDurationMs(row.tcp_ms)}.`,
    );
  } else {
    parts.push(
      `Network inside ${row.site}'s baseline: TTFB ${formatDurationMs(row.ttfb_ms)} against a 7-day p50 of ${formatDurationMs(
        row.site_ttfb_p50_ms,
      )}, TLS ${formatDurationMs(row.tls_ms)}, TCP ${formatDurationMs(row.tcp_ms)}.`,
    );
  }

  // The browser's own telemetry (tiers 4–6), or the explicit absence.
  if (rum) {
    const duration = row.duration_ms;
    const serverShare = rum.server_ms != null && duration > 0 ? rum.server_ms / duration : null;
    const busyShare = rum.long_task_ms != null && duration > 0 ? rum.long_task_ms / duration : null;
    const pathHigh = (rum.ttfb_p50_ms ?? 0) > 1000 || (rum.rtt_ms ?? 0) > 300;
    const overConn = rum.conn_type ? ` over ${rum.conn_type}` : "";
    if (serverShare != null && serverShare >= 0.7) {
      parts.push(
        `The browser's longest single wait on an F&O response in that minute (p95 request duration) was ${formatDurationMs(
          rum.server_ms,
        )} of the ${formatDurationMs(duration)} (${Math.round(
          serverShare * 100,
        )}%) — server side, not the path or the device.`,
      );
    } else if (busyShare != null && busyShare >= 0.5) {
      parts.push(
        `The browser's main thread was busy for ${formatDurationMs(rum.long_task_ms)} of the ${formatDurationMs(
          duration,
        )} in that minute (${Math.round(busyShare * 100)}%) — endpoint: the device or the browser, not F&O.`,
      );
    } else if (pathHigh && (!networkVisible || netState === "normal")) {
      parts.push(
        `Browser-measured path is high: TTFB p50 ${formatDurationMs(rum.ttfb_p50_ms)}, RTT ${formatDurationMs(
          rum.rtt_ms,
        )}${overConn} — the path from this endpoint, ${
          networkVisible
            ? `while Cato's ${row.site} baseline looks normal`
            : "where Cato has no row"
        }.`,
      );
    } else {
      parts.push(
        `Browser telemetry (${formatCount(rum.requests)} requests${
          rum.browser ? `, ${rum.browser}` : ""
        }): TTFB p50 ${formatDurationMs(rum.ttfb_p50_ms)}, ${formatDurationMs(
          rum.server_ms,
        )} longest single F&O wait, ${formatDurationMs(rum.long_task_ms)} main-thread busy, RTT ${formatDurationMs(
          rum.rtt_ms,
        )}${overConn} — none of the three crosses its threshold.`,
      );
    }
  } else {
    parts.push(
      "No browser telemetry for this user in this minute: the fo-rum extension is not installed or not reporting, so server wait, main-thread time and endpoint RTT are unknown — not zero.",
    );
  }

  // Function v4: the browser's own tiers, in the function's precedence, and
  // how the browser minute was joined — an activity-id match is this
  // interaction's own telemetry; a bucket match may hold a neighbour.
  if (rum) {
    const catoNormal = !networkVisible || netState === "normal";
    const tier = rumTierFromBlock(rum, row.duration_ms, catoNormal);
    if (tier === "loaf") {
      parts.push(
        `Long animation frames: the longest frame ran ${formatDurationMs(rum.loaf_max_ms)} of the ${formatDurationMs(
          row.duration_ms,
        )} (${Math.round(((rum.loaf_max_ms ?? 0) / Math.max(1, row.duration_ms)) * 100)}%)${
          rum.loaf_top_script ? `, attributed to ${rum.loaf_top_script}` : ""
        } — ${RUM_TIER_EVIDENCE.loaf}.`,
      );
    } else if (tier === "device_load") {
      parts.push(
        `Device under load: CPU ${formatPctWhole(rum.cpu_pct)}, memory ${formatPctWhole(
          rum.mem_used_pct,
        )} in that minute — ${RUM_TIER_EVIDENCE.device_load}.`,
      );
    } else if (tier === "throttled") {
      // The machine slowed itself down. Say so with the numbers behind it: the
      // charge level if the browser gave one, and the main-thread block, which
      // is what a clocked-down CPU actually costs the person.
      parts.push(
        `The machine was throttling: on battery with power saving${
          rum.battery_pct != null ? ` at ${Math.round(rum.battery_pct)} % charge` : ""
        }, and the browser's main thread was blocked for ${formatDurationMs(
          rum.long_task_ms,
        )} of the ${formatDurationMs(row.duration_ms)} — ${RUM_TIER_EVIDENCE.throttled}.`,
      );
    } else if (tier === "heap_pressure") {
      const pct = heapPct(rum.heap_used_mb, rum.heap_limit_mb);
      parts.push(
        `The browser tab was near its memory limit (${formatMachineMb(
          rum.heap_used_mb,
        )} of ${formatMachineMb(rum.heap_limit_mb)}${
          pct != null ? `, ${Math.round(pct)} %` : ""
        })${rum.device_class ? ` on ${rum.device_class}` : ""} — ${
          RUM_TIER_EVIDENCE.heap_pressure
        }.`,
      );
    } else if (tier === "browser_errors") {
      parts.push(
        `Errors seen in the browser: ${formatCount(rum.status_5xx ?? 0)} 5xx response(s), ${formatCount(
          rum.js_errors ?? 0,
        )} JavaScript error(s)${
          rum.js_error_top_hash ? ` (top template hash ${rum.js_error_top_hash})` : ""
        } — ${RUM_TIER_EVIDENCE.browser_errors}. Error text never leaves the browser.`,
      );
    } else if (tier === "probe_path") {
      parts.push(
        `Idle probe to the app host took ${formatDurationMs(rum.probe_ttfb_ms)} to first byte${
          networkVisible
            ? ` while Cato's ${row.site} baseline looked normal`
            : " where Cato has no row"
        } — ${RUM_TIER_EVIDENCE.probe_path}.`,
      );
    }
    const join = rumJoinKind(rum);
    parts.push(
      join === "activity"
        ? "Browser minute matched by activity id: the block is this interaction's own telemetry."
        : "Browser minute joined by user and 15-minute bucket (no request id matched): it may hold a neighbouring request.",
    );
  }

  // Tier 7: everything visible and normal except this one person.
  if (netState === "normal" && networkVisible && peersNormal && row.exceptions.length === 0) {
    parts.push(
      "Only this user was slow with a normal path and normal peers — look at the device and browser before the server.",
    );
  }

  // Tier 8: neither layer visible — say which of the three UNATTRIBUTED
  // sub-labels applies, so the closing sentence matches the chip.
  if (!networkVisible && !rum && !peersSlow && !peersLikely && row.exceptions.length === 0) {
    if (peersNormal) {
      parts.push(
        `Unattributed — ${UNATTRIBUTED_SUBLABELS.peers_normal}: neither is visible without a Cato row or browser telemetry.`,
      );
    } else if (peerUsers < 3) {
      parts.push(
        `Unattributed — ${UNATTRIBUTED_SUBLABELS.too_few_peers}: ${formatCount(
          peerUsers,
        )} other user(s) ran this form in the window, too few to compare, and neither the network nor the browser layer is visible.`,
      );
    } else {
      parts.push(
        `Unattributed — ${UNATTRIBUTED_SUBLABELS.not_visible}: peers give no clear signal and no browser reported, so no layer can be named.`,
      );
    }
  }

  return parts;
}

/** The reason paragraph for one record. */
export function deriveRecordReason(row: FoConsoleSlowRow): string {
  return deriveRecordReasonParts(row).join(" ");
}

/* ------------------------------------------------------------------------ *
 * Collector status
 * ------------------------------------------------------------------------ */

/** `StatusStrip.Cell` tone vocabulary. */
export type StripTone = "positive" | "caution" | "negative" | "neutral";

/** Expected cadence per task, for staleness: DEM every 15 min, identity daily. */
export function collectTaskCadenceMs(name: string): number {
  return name.toLowerCase().startsWith("identity") ? 24 * HOUR_MS : 15 * 60_000;
}

export function collectTaskLabel(name: string): string {
  const lower = name.toLowerCase();
  if (lower.startsWith("dem")) {
    return "Cato DEM (every 15 min)";
  }
  if (lower.startsWith("identity")) {
    return "Identity map (daily)";
  }
  return name;
}

export interface CollectTaskVerdict {
  status: StripTone;
  /** One clause for the cell title and the caption. */
  detail: string;
}

/** A task's strip tone: errored → negative, not configured/enabled → neutral, late → caution. */
export function collectTaskVerdict(
  task: FoConsoleCollectTask,
  now: number = Date.now(),
): CollectTaskVerdict {
  if (task.error) {
    return { status: "negative", detail: `last run failed: ${task.error}` };
  }
  if (!task.configured) {
    return { status: "neutral", detail: "not configured — nothing has been asked of the source" };
  }
  if (!task.enabled) {
    return { status: "neutral", detail: "disabled" };
  }
  if (!task.last_run_at) {
    return { status: "caution", detail: "enabled but has not run yet" };
  }
  const ran = Date.parse(task.last_run_at);
  if (!Number.isFinite(ran)) {
    return { status: "caution", detail: "last run time unreadable" };
  }
  const late = now - ran > 2 * collectTaskCadenceMs(task.name);
  return late
    ? { status: "caution", detail: "overdue — more than two cadences since the last run" }
    : { status: "positive", detail: "running on cadence" };
}

/* ------------------------------------------------------------------------ *
 * Small helpers the panels share
 * ------------------------------------------------------------------------ */

/**
 * Linear bin of `value` within `[min, max]` onto the grid's five-step ramp for
 * `cellScale`. `undefined` (not band 1) when there is no value — a blank and a
 * low value are different facts.
 */
export function heatBand(
  value: number | null | undefined,
  min: number,
  max: number,
): 1 | 2 | 3 | 4 | 5 | undefined {
  if (value == null || !Number.isFinite(value)) {
    return undefined;
  }
  if (max <= min) {
    return 3;
  }
  const t = (value - min) / (max - min);
  const band = Math.min(5, Math.max(1, Math.ceil(t * 5)));
  return (band === 0 ? 1 : band) as 1 | 2 | 3 | 4 | 5;
}

/** The HTTP status carried by a thrown `ApiError`, or `null` for anything else. */
export function apiErrorStatus(error: unknown): number | null {
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as { status: unknown }).status;
    return typeof status === "number" ? status : null;
  }
  return null;
}

/** FastAPI's `{ detail }` from a thrown `ApiError` body, when it is a string. */
export function apiErrorDetail(error: unknown): string | null {
  if (error && typeof error === "object" && "body" in error) {
    const body = (error as { body: unknown }).body;
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as { detail: unknown }).detail;
      return typeof detail === "string" ? detail : null;
    }
  }
  return null;
}

/** Parse an F&O URL's `cmp` (legal entity) and `mi` (form) — the Hub tile's parser, shared. */
export function parseFoUrl(url: string): { legal_entity: string | null; form: string | null } {
  try {
    const parsed = new URL(url);
    return {
      legal_entity: parsed.searchParams.get("cmp"),
      form: parsed.searchParams.get("mi"),
    };
  } catch {
    return { legal_entity: null, form: null };
  }
}

/* ------------------------------------------------------------------------ *
 * Pinpoint — the ranked "what to fix, and what it costs" vocabulary
 * ------------------------------------------------------------------------ */

/**
 * The four questions a finding can answer, plus the fold.
 *
 * Same shape as {@link CLASSIFICATION_FAMILIES} and for the same reason: the
 * backend can add a kind without a frontend deploy, so an unrecognised string
 * folds into `other` and renders itself rather than throwing (design rules §8 —
 * no enum that mirrors live data).
 */
export const PINPOINT_KINDS = [
  "company_form",
  "form_all",
  "regression",
  "aos_node",
  "other",
] as const;

export type PinpointKind = (typeof PINPOINT_KINDS)[number];

/** The kind a finding carries; anything unrecognised folds to `other`. */
export function pinpointKind(kind: string | null | undefined): PinpointKind {
  const value = (kind ?? "").trim().toLowerCase();
  const match = PINPOINT_KINDS.find((known) => known === value);
  return match ?? "other";
}

/** What the chip says. Names the question the finding answers, not the metric. */
export const PINPOINT_KIND_LABEL: Record<PinpointKind, string> = {
  company_form: "One company",
  form_all: "Every company",
  regression: "Regression",
  aos_node: "One AOS node",
  other: "Other",
};

/** The clause under the label — what qualified the finding for this kind. */
export const PINPOINT_KIND_DETAIL: Record<PinpointKind, string> = {
  company_form: "slow in this company, normal in its peers",
  form_all: "slow wherever it is opened, so a form-level defect",
  regression: "the same cell was fine one window earlier",
  aos_node: "one AOS instance slower than the rest of the farm",
  other: "a kind this board has not been taught yet — the backend named it, so it is shown as sent",
};

/**
 * The layers a verdict can point at, plus the fold.
 *
 * `application_company` and `application_all` are one layer with two scopes:
 * both are HSO's ticket, and only the blast radius differs.
 */
export const PINPOINT_VERDICTS = [
  "application_company",
  "application_all",
  "server",
  "path",
  "device",
  "browser_tab",
  "unattributed",
  "other",
] as const;

export type PinpointVerdict = (typeof PINPOINT_VERDICTS)[number];

/** The verdict a finding carries; anything unrecognised folds to `other`. */
export function pinpointVerdict(verdict: string | null | undefined): PinpointVerdict {
  const value = (verdict ?? "").trim().toLowerCase();
  const match = PINPOINT_VERDICTS.find((known) => known === value);
  return match ?? "other";
}

export const PINPOINT_VERDICT_LABEL: Record<PinpointVerdict, string> = {
  application_company: "Application, this company",
  application_all: "Application, every company",
  server: "Server (F&O's own timing)",
  path: "Network path",
  device: "Device",
  browser_tab: "Browser tab",
  unattributed: "Unattributed",
  other: "Unclassified verdict",
};

/**
 * The classification family a verdict belongs to, so the pinpoint chips and the
 * slow section's chips speak one colour language: a `path` verdict here wears
 * the same tone as a `NETWORK/...` classification there. Deriving the tone this
 * way rather than enumerating a second tone map is what keeps the two screens
 * from drifting apart the next time a family is re-toned.
 */
export function pinpointVerdictFamily(verdict: string | null | undefined): ClassificationFamily {
  switch (pinpointVerdict(verdict)) {
    case "application_company":
    case "application_all":
      return "APPLICATION";
    case "server":
      return "SERVER";
    case "path":
      return "NETWORK";
    case "device":
      return "ENDPOINT";
    case "browser_tab":
      return "BROWSER";
    case "unattributed":
      return "UNATTRIBUTED";
    default:
      return "OTHER";
  }
}

/** Chip tone for a verdict — the family's tone, never a severity of its own. */
export function pinpointVerdictTone(verdict: string | null | undefined): ChipTone {
  return CLASSIFICATION_TONE[pinpointVerdictFamily(verdict)];
}

/**
 * The one sentence that says what `excess_hours` is, in words.
 *
 * It sits on the board because the figure is the ranking key and a number
 * labelled only "hours" invites the reader to guess. Kept here so the KPI tile,
 * the findings list and the drawer cannot word it three different ways.
 */
export const EXCESS_HOURS_MEANING =
  "Person-hours people spent waiting above what the same form costs elsewhere — the time a fix " +
  "gives back, and the order this board ranks in.";

/**
 * Person-hours as a duration a human feels — `12.4 h`, one decimal.
 *
 * `0` is printed as `0.0 h` on purpose: on a company row a zero is measured
 * ("nothing about this company cleared the floors"), and only a `null` is
 * unknown. That is the whole em-dash rule, applied the other way round.
 */
export function formatExcessHours(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return `${value.toFixed(1)} h`;
}

/** A peer ratio as `3.5×`; an em dash where the finding has no peer to compare against. */
export function formatRatio(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return `${value.toFixed(1)}×`;
}

/**
 * A share of a whole as `17 of 269,725 (<0.1 %)`.
 *
 * The `<0.1 %` form is the honesty rule doing real work: browser telemetry
 * covered 17 of 269,725 form loads when this board was written, and one decimal
 * place would print `0.0 %` — which reads as "none" for a number that is not
 * zero. A zero numerator still prints `0 %`, because that one *is* none.
 */
export function formatShareOf(part: number | null | undefined, total: number | null | undefined) {
  if (part == null || total == null) {
    return EM_DASH;
  }
  const counts = `${formatCount(part)} of ${formatCount(total)}`;
  if (total <= 0) {
    return `${counts} (no denominator)`;
  }
  // A part larger than its whole is not a share, it is a broken input — and on
  // a board whose whole premise is honesty about partial data, "185 of 147" is
  // the one thing that must never be printed as though it were a fact. Say it
  // is impossible rather than computing 126 % from it.
  if (part > total) {
    return `${counts} — impossible, the parts exceed the whole`;
  }
  const pct = (part / total) * 100;
  if (part > 0 && pct < 0.1) {
    return `${counts} (<0.1 %)`;
  }
  return `${counts} (${pct.toFixed(1)} %)`;
}

/** A share as a 0–100 percentage, or `null` when there is nothing to divide by. */
export function sharePct(
  part: number | null | undefined,
  total: number | null | undefined,
): number | null {
  if (part == null || total == null || total <= 0) {
    return null;
  }
  return (part / total) * 100;
}

/** The words a coverage figure wears when the panel never measured it. */
export const NOT_MEASURED = "not measured";

/**
 * The two browser-coverage lines, or the "not measured" pair.
 *
 * `coverage: null` is the panel saying the coverage query did not answer. It is
 * unknown, and a `0 of 0 (0 %)` in its place would be a fabricated zero — the
 * exact flattering direction the design rules forbid (§5).
 */
export function pinpointCoverageLines(coverage: FoConsolePinpointCoverage | null | undefined) {
  if (coverage == null) {
    return {
      measured: false as const,
      companies: NOT_MEASURED,
      companiesPct: null,
      companiesPctText: NOT_MEASURED,
      events: NOT_MEASURED,
      eventsPct: null,
    };
  }
  const companiesPct = sharePct(coverage.companies_with_rum, coverage.companies);
  return {
    measured: true as const,
    companies: formatShareOf(coverage.companies_with_rum, coverage.companies),
    companiesPct,
    //: The headline the KPI tile shows on its own. A share this small must not
    //: round to "0 %" — that reads as "none reported", which is a different
    //: fact from "almost none did".
    companiesPctText:
      companiesPct == null
        ? NOT_MEASURED
        : companiesPct > 0 && companiesPct < 0.1
          ? "<0.1 %"
          : `${companiesPct.toLocaleString(undefined, { maximumFractionDigits: 1 })} %`,
    events: formatShareOf(coverage.events_with_rum, coverage.events),
    eventsPct: sharePct(coverage.events_with_rum, coverage.events),
  };
}

/**
 * The sentence that has to appear beside every finding on this board.
 *
 * Server-side timing covers the whole estate; browser telemetry covers whoever
 * runs the extension, and today that is a rounding error. Saying so is what
 * stops a reader taking a verdict for something the fleet measured.
 */
export function pinpointEvidenceBasis(
  coverage: FoConsolePinpointCoverage | null | undefined,
): string {
  const lines = pinpointCoverageLines(coverage);
  if (!lines.measured) {
    return (
      "Every finding here rests on F&O's own server-side timing, which covers every company. " +
      "How much browser telemetry sits behind them was not measured this time — unknown, not zero."
    );
  }
  return (
    "Every finding here rests on F&O's own server-side timing, which covers every company. " +
    `Browser telemetry — what the tab itself measured — covers ${lines.companies} companies and ` +
    `${lines.events} form loads, so the fleet is not instrumented and nothing on this board ` +
    "depends on it being."
  );
}

/** Distinct company codes named by a set of findings; `form_all` names none. */
export function pinpointCompaniesAffected(
  findings: readonly Pick<FoConsolePinpointFinding, "company">[],
): number {
  const codes = new Set<string>();
  for (const finding of findings) {
    const code = finding.company.trim();
    if (code !== "") {
      codes.add(code);
    }
  }
  return codes.size;
}

/** Person-hours across a set of findings — the board's headline figure. */
export function pinpointTotalExcessHours(
  findings: readonly Pick<FoConsolePinpointFinding, "excess_hours">[],
): number {
  return findings.reduce((sum, finding) => sum + (finding.excess_hours || 0), 0);
}

/**
 * The case-fold caption for a company row.
 *
 * A `case_variants` of 2 means the legal-entity code arrives written two ways
 * (`USL` and `usl`) and was counted as two companies until the fold landed on
 * 2026-08-21. It is a data-quality finding about the telemetry, not noise, so it
 * is stated on the row rather than smoothed away.
 */
export function caseVariantCaption(row: Pick<FoConsoleCompanyLoad, "company" | "case_variants">) {
  if (row.case_variants <= 1) {
    return null;
  }
  return (
    `${row.company} arrives in ${row.case_variants} spellings that differ only in case. They are ` +
    "one legal entity, folded into this row; before the fold each spelling counted as its own " +
    "company, so every per-company number was split between them."
  );
}

/**
 * The short name a finding wears on a chart axis or a table cell.
 *
 * The form plus the legal entity is what an operator recognises. An `aos_node`
 * finding names no form, so it falls back to the backend's own sentence rather
 * than to an empty label — a blank axis tick would be a finding nobody can act
 * on.
 */
export function pinpointFindingLabel(
  finding: Pick<FoConsolePinpointFinding, "form" | "company" | "title">,
): string {
  const form = finding.form.trim();
  const company = finding.company.trim();
  if (form === "") {
    return finding.title;
  }
  return company === "" ? form : `${form} · ${company}`;
}

/** Person-hours per kind across a set of findings, in fixed kind order, zero-sum kinds dropped. */
export function pinpointHoursByKind(
  findings: readonly Pick<FoConsolePinpointFinding, "kind" | "excess_hours">[],
): { kind: PinpointKind; hours: number }[] {
  const byKind = new Map<PinpointKind, number>();
  for (const finding of findings) {
    const kind = pinpointKind(finding.kind);
    byKind.set(kind, (byKind.get(kind) ?? 0) + (finding.excess_hours || 0));
  }
  return PINPOINT_KINDS.filter((kind) => (byKind.get(kind) ?? 0) > 0).map((kind) => ({
    kind,
    hours: byKind.get(kind) ?? 0,
  }));
}
